import { useEffect, useState } from "react";
import { ShieldCheck, Download, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls } from "@/lib/studioApi";

const Card = ({ title, icon: Icon, children, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-[#E2E7F7] bg-white p-5 sm:p-6">
    <h2 className="font-heading flex items-center gap-2 text-base font-bold text-[#0F2645]"><Icon className="h-4 w-4 text-[#3A5CC8]" /> {title}</h2>
    {children}
  </div>
);

export const SecuritySettings = () => {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const load = () => Promise.all([studio.get("/security"), studio.get("/gdpr/requests").catch(() => ({ data: [] }))]).then(([a, b]) => { setData(a.data); setRequests(b.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleMfa = async (enabled) => {
    try { await studio.put("/security", { mfa_enabled: enabled }); toast.success(enabled ? "Double authentification activée" : "Double authentification désactivée"); load(); }
    catch (e) { toast.error(apiError(e, "Impossible de modifier le réglage.")); }
  };
  const forget = async () => {
    try { await studio.delete("/security/devices"); toast.success("Appareils oubliés : reconnectez-vous partout."); localStorage.removeItem("devisto_device"); load(); }
    catch (e) { toast.error(apiError(e, "Action impossible.")); }
  };
  const exportData = async () => {
    try {
      const res = await studio.get("/gdpr/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "devisto-export.json"; a.click(); URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error(apiError(e, "Export impossible.")); }
  };
  const requestDeletion = async () => {
    try { await studio.post("/gdpr/delete-request", { reason, confirm_email: confirmEmail }); toast.success("Demande enregistrée : traitement sous 30 jours maximum."); setConfirmEmail(""); setReason(""); load(); }
    catch (e) { toast.error(apiError(e, "Demande impossible.")); }
  };
  if (!data) return null;
  const pending = requests.find((r) => r.status === "pending");

  return (
    <div className="grid gap-4">
      <Card title="Sécurité du compte" icon={ShieldCheck} testId="security-card">
        <label className="mt-4 flex items-start gap-3 text-sm">
          <input type="checkbox" data-testid="security-mfa-toggle" className="mt-1 h-4 w-4 accent-[#3A5CC8]" checked={!!data.mfa_enabled} onChange={(e) => toggleMfa(e.target.checked)} />
          <span><span className="font-semibold">Double authentification par e-mail</span><br /><span className="text-[#5C6A87]">Un code à 6 chiffres est demandé à chaque connexion depuis un nouvel appareil (mémorisé 90 jours).</span></span>
        </label>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#5C6A87]">
          <span data-testid="security-devices-count">{data.devices.length} appareil(s) de confiance</span>
          <button type="button" data-testid="security-forget-devices" onClick={forget} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E7F7] px-3 py-1.5 font-semibold text-[#0F2645] hover:bg-[#F6F8FD]"><LogOut className="h-3.5 w-3.5" /> Déconnecter tous les appareils</button>
        </div>
        {data.logins.length > 0 && (
          <ul data-testid="security-logins" className="mt-4 divide-y divide-[#E2E7F7] text-xs text-[#5C6A87]">
            {data.logins.slice(0, 5).map((l, i) => <li key={i} className="flex justify-between py-1.5"><span>{{ login_ok: "Connexion réussie", login_failed: "Échec de connexion", mfa_failed: "Code MFA incorrect" }[l.action]} · {l.ip}</span><span>{new Date(l.at).toLocaleString("fr-BE")}</span></li>)}
          </ul>
        )}
      </Card>
      <Card title="Mes données (RGPD)" icon={Download} testId="gdpr-card">
        <p className="mt-2 text-sm text-[#5C6A87]">Téléchargez une copie complète de vos données (clients, devis, factures, interventions, journal d'accès) ou demandez la suppression de votre compte. Les factures et pièces comptables restent archivées 10 ans (obligation légale) ; tout le reste est supprimé.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" data-testid="gdpr-export-btn" onClick={exportData} className="inline-flex items-center gap-2 rounded-xl bg-[#0F2645] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1B3764]"><Download className="h-4 w-4" /> Exporter mes données</button>
        </div>
        {pending ? (
          <p data-testid="gdpr-pending" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Demande de suppression en cours (déposée le {new Date(pending.created_at).toLocaleDateString("fr-BE")}, traitement au plus tard le {pending.due_at.slice(0, 10)}).</p>
        ) : (
          <details className="mt-4 text-sm">
            <summary data-testid="gdpr-delete-toggle" className="cursor-pointer font-semibold text-red-700">Demander la suppression de mon compte</summary>
            <div className="mt-3 grid gap-2">
              <input data-testid="gdpr-reason" className={inputCls} placeholder="Motif (facultatif)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <input data-testid="gdpr-confirm-email" className={inputCls} placeholder="Tapez l'e-mail du compte pour confirmer" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} />
              <button type="button" data-testid="gdpr-delete-btn" disabled={!confirmEmail} onClick={requestDeletion} className="inline-flex w-fit items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"><Trash2 className="h-4 w-4" /> Envoyer la demande</button>
            </div>
          </details>
        )}
      </Card>
    </div>
  );
};
